import { api } from './api';

export interface NotificationCounts {
  comments: number;
  request_details: number;
  reassigned: number;
  total: number;
}

/** List item from GET /notifications — body text omitted; use GET /notifications/:id for full message. */
export interface NotificationListItem {
  id: string;
  type: string;
  title: string;
  is_read: boolean;
  priority?: string;
  created_at: string;
  metadata?: unknown;
}

export interface Notification extends NotificationListItem {
  message?: string;
  read_at?: string;
}

class NotificationService {
  /**
   * Mark a notification as viewed
   */
  async markAsViewed(notificationId: string, entityType?: string, entityId?: string): Promise<void> {
    const response = await api.post('/notifications/view', {
      notification_id: notificationId,
      entity_type: entityType,
      entity_id: entityId
    });
    return response.data;
  }

  /**
   * Get unread notification counts
   */
  async getUnreadCounts(): Promise<NotificationCounts> {
    const response = await api.get('/notifications/counts');
    return response.data.data;
  }

  /**
   * Get unread notifications for a specific entity
   */
  async getUnreadForEntity(entityType: string, entityId: string): Promise<Notification[]> {
    const response = await api.get(`/notifications/entity/${entityType}/${entityId}`);
    return response.data.data;
  }

  /**
   * Mark all notifications for an entity as viewed
   */
  async markEntityAsViewed(entityType: string, entityId: string): Promise<void> {
    const response = await api.post(`/notifications/entity/${entityType}/${entityId}/view`);
    return response.data;
  }

  /**
   * Get all notifications for a user
   */
  async getNotifications(params: {
    page: number;
    limit: number;
    is_read?: boolean | undefined;
  }): Promise<{
    data: {
      notifications: Notification[];
      unread_count: number;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
  }> {
    const queryParams = new URLSearchParams({
      page: params.page.toString(),
      limit: params.limit.toString(),
      ...(params.is_read !== undefined && { is_read: params.is_read.toString() })
    });

    const response = await api.get(`/notifications?${queryParams}`);
    return response.data;
  }

  /** Full notification including message body — GET /notifications/:id */
  async getNotificationById(id: string): Promise<Notification> {
    const response = await api.get(`/notifications/${id}`);
    const payload = response.data?.data ?? response.data;
    return payload as Notification;
  }

  /**
   * Get unread count for a user (legacy method)
   */
  async getUnreadCount(): Promise<number> {
    const counts = await this.getUnreadCounts();
    return counts.total;
  }

  /**
   * Mark a notification as read (legacy method)
   */
  async markAsRead(notificationId: string): Promise<void> {
    await this.markAsViewed(notificationId);
  }

  /**
   * Mark all notifications as read (legacy method)
   */
  async markAllAsRead(): Promise<void> {
    const response = await api.post('/notifications/mark-all-read');
    return response.data;
  }

  /**
   * Delete a notification (legacy method)
   */
  async deleteNotification(notificationId: string): Promise<void> {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  }

  /**
   * Clear all notifications (legacy method)
   */
  async clearAllNotifications(): Promise<void> {
    const response = await api.delete('/notifications/clear-all');
    return response.data;
  }

  /**
   * Get navigation data for a notification (legacy method)
   */
  getNotificationNavigationData(notification: any): {
    path: string;
    entityId?: string;
    entityType?: string;
  } {
    const metadata = notification.metadata || {};
    
    // Default navigation paths based on notification type
    if (notification.type.includes('approval')) {
      return {
        path: '/approvals',
        entityId: metadata.entity_id,
        entityType: metadata.entity_type
      };
    } else if (notification.type.includes('transaction')) {
      return {
        path: '/financial/transactions',
        entityId: metadata.entity_id,
        entityType: 'transaction'
      };
    } else if (notification.type.includes('gst')) {
      return {
        path: '/gst/compliance',
        entityId: metadata.entity_id,
        entityType: 'gst_filing'
      };
    } else if (notification.type.includes('contact')) {
      return {
        path: '/contacts',
        entityId: metadata.entity_id,
        entityType: 'contact'
      };
    }
    
    return {
      path: '/notifications'
    };
  }

  /**
   * Format notification message (legacy method)
   */
  formatNotificationMessage(notification: any): string {
    if (notification.message) {
      return notification.message;
    }
    
    // Fallback formatting based on notification type
    switch (notification.type) {
      case 'approval_comment':
        return 'New comment added to approval';
      case 'approval_request_details':
        return 'Additional details requested for approval';
      case 'approval_reassigned':
        return 'Approval has been reassigned';
      case 'approval_processed':
        return 'Approval has been processed';
      default:
        return notification.title || 'New notification';
    }
  }
}

const notificationService = new NotificationService();
export default notificationService;
export { notificationService };